"use client";

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  Line,
  LineChart,
  Pie,
  PieChart,
  ReferenceArea,
  ReferenceLine,
  XAxis,
  YAxis
} from "@cmp-src/primitives/chart";
import type {
  ReportMeasure,
  ReportParts,
  ReportPeriod,
  ReportPoint,
  ReportQuery,
  ReportQueryResult,
  ReportRowOrder,
  ReportTotals,
  ReportTrendMode
} from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import React, { useMemo, useState } from "react";
import {
  type FoldedRow,
  foldRows,
  OTHER_COLOR,
  OTHER_KEY,
  type StackEntry,
  type StackSeries,
  seriesColor,
  sortRows,
  stackData,
  zoomPeriod
} from "../lib/chartData";
import type { ChartSettings } from "../lib/chartSettings";
import { lowerFirst } from "../lib/describeQuery";
import {
  formatDelta,
  formatParts,
  formatValue,
  partsUnit
} from "../lib/formatValue";
import {
  OWN_KEY,
  type PairedResult,
  pairedRowValue,
  pairRatio,
  pairSlices,
  rowsByKey,
  seriesByBucket
} from "../lib/pair";
import type { ChartForm } from "../lib/pickForm";
import {
  annotationBucket,
  calendarWeeks,
  cumulative,
  smoothed,
  smoothingWindow
} from "../lib/shapeData";
import {
  bucketLabel,
  bucketTitle,
  CURRENT,
  CURRENT_SOFT,
  PAIRED,
  PREVIOUS,
  Segmented,
  SeriesLegend,
  TooltipBox,
  TooltipRow,
  type TrendPoint,
  TrendTooltip
} from "./ChartBits";
import { ReportEmptyState } from "./ReportEmptyState";

// Bars use one hue (ranked magnitude), the grid and the calendar one blue ramp.
const RAMP = ["#cde2fb", "#9ec5f4", "#6da7ec", "#3987e5", "#256abf", "#184f95"];
const EMPTY_CELL = "#eef0f2";
const ANNOTATION = "#e6a23c";

/** How many members are drawn before the rest fold into "Other". */
const BAR_FOLD = 10;
const STACK_ENTRIES = 12;
const STACK_SERIES = 8;
const SHARE_SLICES = 8;

interface ResultChartProps {
  form: ChartForm;
  result: ReportQueryResult;
  /** The question behind the result, which names the unit of a ratio's parts. */
  query?: ReportQuery;
  /** A tile's number to reach, in the measure's unit; a gauge marks it. */
  target?: number | null;
  /**
   * A second measure's answer: drawn beside the first's on a trend or a bar
   * list, as the two slices of a share, and as a share of the first on a
   * gauge.
   */
  paired?: PairedResult;
  /** Click a bar or cell to zoom into that member. */
  onDrill: (dimension: string, memberId: string) => void;
  /** Drag across the trend, or click a calendar day, to narrow the period. */
  onZoom?: (period: ReportPeriod) => void;
  /** What the chart remembers; kept by the caller when it wants to save it. */
  settings?: ChartSettings;
  onSettingsChange?: (patch: Partial<ChartSettings>) => void;
  /** Plot height of a trend in pixels; the default suits a full-width tile. */
  height?: number;
}

type SettingsProps = Pick<ResultChartProps, "settings" | "onSettingsChange">;

/**
 * A chart setting: read from and written to the caller's settings when it
 * keeps them, else kept here. A saved setting seeds the local one, and a
 * newly saved one (a tile edited on a board) replaces it.
 */
const useSetting = <K extends keyof ChartSettings>(
  key: K,
  fallback: NonNullable<ChartSettings[K]>,
  { settings, onSettingsChange }: SettingsProps
): [
  NonNullable<ChartSettings[K]>,
  (value: NonNullable<ChartSettings[K]>) => void
] => {
  const saved = settings?.[key] as NonNullable<ChartSettings[K]> | undefined;
  const [local, setLocal] = useState<NonNullable<ChartSettings[K]>>(
    saved ?? fallback
  );
  // Compared by value: a saved list is rebuilt on every render of the tile.
  const savedKey = JSON.stringify(saved ?? null);
  const [seenKey, setSeenKey] = useState(savedKey);
  if (!onSettingsChange && savedKey !== seenKey) {
    setSeenKey(savedKey);
    setLocal(saved ?? fallback);
  }
  const value = onSettingsChange ? (saved ?? fallback) : local;
  const set = (next: NonNullable<ChartSettings[K]>) => {
    if (onSettingsChange) {
      onSettingsChange({ [key]: next } as Partial<ChartSettings>);
    } else {
      setLocal(next);
    }
  };
  return [value, set];
};

const toggled = (list: string[], key: string): string[] =>
  list.includes(key) ? list.filter((k) => k !== key) : [...list, key];

const Empty: React.FC = () => (
  <ReportEmptyState
    title="Nothing in this period"
    hint="Try a longer period, or drop a filter that narrows it to nothing."
  />
);

/** "312 / 1,040": the numbers behind a ratio cell, short. */
const shortParts = (
  measure: Pick<ReportMeasure, "parts">,
  parts: ReportParts | null
): string | null =>
  measure.parts && parts
    ? `${Math.round(parts.numerator).toLocaleString("en-US")} / ${Math.round(parts.denominator).toLocaleString("en-US")}`
    : null;

/** The `entry` key of the data row a bar or slice was drawn from. */
const keyOfItem = (item: unknown, field: string): string => {
  if (typeof item !== "object" || item === null) {
    return "";
  }
  const record = item as Record<string, unknown>;
  const nested = record.payload;
  const candidate =
    typeof nested === "object" && nested !== null
      ? (nested as Record<string, unknown>)[field]
      : record[field];
  return typeof candidate === "string" ? candidate : "";
};

/**
 * Drills into a folded row: one member per breakdown, so a row of a
 * two-dimensional result drawn as a list pins both.
 */
const drillRow = (
  result: Pick<ReportQueryResult, "columns">,
  row: Pick<FoldedRow, "keys">,
  onDrill: (dimension: string, memberId: string) => void
): void => {
  const dimensions = result.columns.filter((c) => c.type === "dimension");
  dimensions.forEach((c, i) => {
    if (row.keys[i]) {
      onDrill(c.key, row.keys[i]);
    }
  });
};

/** The tooltip a bar or a cell carries: label, value, the numbers behind it, the previous period. */
const rowTitle = (
  measure: ReportMeasure,
  label: string,
  row: Pick<FoldedRow, "value" | "previous" | "parts">,
  unit?: string
): string => {
  const lines = [`${label}: ${formatValue(measure, row.value)}`];
  const parts = formatParts(measure, row.parts, unit);
  if (parts) {
    lines.push(parts);
  }
  if (row.previous !== null) {
    lines.push(`Previous period: ${formatValue(measure, row.previous)}`);
  }
  return lines.join("\n");
};

const trendConfig: ChartConfig = {
  value: { label: "Current", color: CURRENT },
  previous: { label: "Previous", color: PREVIOUS }
};

const TREND_MODES: { key: ReportTrendMode; label: string }[] = [
  { key: "raw", label: "Actual" },
  { key: "cumulative", label: "Cumulative" },
  { key: "smoothed", label: "Smoothed" }
];

interface TrendChartProps extends SettingsProps {
  result: ReportQueryResult;
  query?: ReportQuery;
  paired?: PairedResult;
  onZoom?: (period: ReportPeriod) => void;
  height?: number;
}

const TREND_HEIGHT = 320;

/** A drag across buckets, as their indexes. */
type Drag = { from: number; to: number };

const activeIndex = (
  state: { activeTooltipIndex?: number } | null | undefined
): number | null =>
  typeof state?.activeTooltipIndex === "number"
    ? state.activeTooltipIndex
    : null;

/**
 * The measure over time, with the previous period dashed, or a second
 * measure beside it (a pair is its own comparison, so the previous period
 * stays out). It reads as the actual values, a running total (counts) or a
 * moving average; days the inventory changed are marked; a drag narrows the
 * period.
 */
const TrendChart: React.FC<TrendChartProps> = ({
  result,
  query,
  paired,
  onZoom,
  settings,
  onSettingsChange,
  height = TREND_HEIGHT
}) => {
  const measure = result.meta.measure;
  const grain = result.meta.grain;
  const unit = partsUnit(measure, query);
  const hasPrevious = result.totals.previous !== null && !paired;
  const [mode, setMode] = useSetting("trend", "raw", {
    settings,
    onSettingsChange
  });
  const [drag, setDrag] = useState<Drag | null>(null);
  const canCumulate = measure.kind === "count";
  const shownMode = mode === "cumulative" && !canCumulate ? "raw" : mode;
  const shaped = (points: ReportPoint[]): ReportPoint[] =>
    shownMode === "cumulative"
      ? cumulative(points)
      : shownMode === "smoothed"
        ? smoothed(points, smoothingWindow(grain))
        : points;
  const series = shaped(result.series);
  const pairedPoints = paired
    ? seriesByBucket(shaped(paired.result.series))
    : null;
  const data: TrendPoint[] = series.map((p) => ({
    bucket: p.bucket,
    value: p.value,
    previous: hasPrevious ? (p.previous ?? undefined) : undefined,
    parts: p.parts,
    paired: pairedPoints?.get(p.bucket)?.value
  }));
  const config: ChartConfig = paired
    ? {
        value: { label: measure.label, color: CURRENT },
        paired: { label: paired.measure.label, color: PAIRED }
      }
    : trendConfig;
  const buckets = data.map((d) => d.bucket);
  // Annotations land on the bucket their day falls in.
  const notes = (result.annotations ?? []).flatMap((a) => {
    const bucket = annotationBucket(buckets, a);
    return bucket ? [{ ...a, bucket }] : [];
  });
  const notesByBucket: Record<string, string[]> = {};
  for (const note of notes) {
    notesByBucket[note.bucket] = [
      ...(notesByBucket[note.bucket] ?? []),
      note.label
    ];
  }

  if (data.length === 0) {
    return <Empty />;
  }

  const canZoom = !!onZoom && data.length > 1;
  const selection =
    drag && drag.from !== drag.to
      ? [Math.min(drag.from, drag.to), Math.max(drag.from, drag.to)]
      : null;
  const finishDrag = () => {
    if (onZoom && selection) {
      const period = zoomPeriod(
        buckets,
        selection[0],
        selection[1],
        grain,
        result.meta.to
      );
      if (period) {
        onZoom(period);
      }
    }
    setDrag(null);
  };
  const modeNote =
    shownMode === "cumulative"
      ? "Running total"
      : shownMode === "smoothed"
        ? `${smoothingWindow(grain)}-${grain} average`
        : undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-end gap-3">
        {paired ? (
          <SeriesLegend
            className="mr-auto"
            entries={[
              { key: "value", label: measure.label, color: CURRENT },
              { key: "paired", label: paired.measure.label, color: PAIRED }
            ]}
          />
        ) : null}
        {result.series.length > 2 ? (
          <Segmented
            options={TREND_MODES.filter(
              (m) => m.key !== "cumulative" || canCumulate
            )}
            value={shownMode}
            onChange={setMode}
          />
        ) : null}
      </div>
      <ChartContainer
        config={config}
        className={cn("w-full", canZoom && "select-none")}
        // The caller sizes a compact trend (a card) below the tile default.
        style={{ height }}
      >
        <LineChart
          data={data}
          margin={{ top: 12, right: 16, left: 0, bottom: 0 }}
          onMouseDown={(state) => {
            const index = activeIndex(state);
            if (canZoom && index !== null) {
              setDrag({ from: index, to: index });
            }
          }}
          onMouseMove={(state) => {
            const index = activeIndex(state);
            if (drag && index !== null && index !== drag.to) {
              setDrag({ from: drag.from, to: index });
            }
          }}
          onMouseUp={finishDrag}
          onMouseLeave={() => setDrag(null)}
        >
          <CartesianGrid vertical={false} stroke="#f0f0f0" />
          <XAxis
            dataKey="bucket"
            tickFormatter={(bucket: string) => bucketLabel(bucket, grain)}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11, fill: "#a3a3a3" }}
            minTickGap={24}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            width={44}
            tick={{ fontSize: 11, fill: "#a3a3a3" }}
            tickFormatter={(v: number) => formatValue(measure, v)}
          />
          <ChartTooltip
            cursor={{ stroke: "#d4d4d4" }}
            content={
              <TrendTooltip
                measure={measure}
                unit={unit}
                grain={grain}
                notes={notesByBucket}
                modeNote={modeNote}
                labels={
                  paired
                    ? { own: measure.label, paired: paired.measure.label }
                    : undefined
                }
              />
            }
          />
          {selection ? (
            <ReferenceArea
              x1={data[selection[0]].bucket}
              x2={data[selection[1]].bucket}
              fill={CURRENT}
              fillOpacity={0.12}
              strokeOpacity={0}
            />
          ) : null}
          {Object.keys(notesByBucket).map((bucket) => (
            <ReferenceLine
              key={bucket}
              x={bucket}
              stroke={ANNOTATION}
              strokeDasharray="3 3"
            />
          ))}
          {hasPrevious ? (
            <Line
              dataKey="previous"
              type="monotone"
              stroke="var(--color-previous)"
              strokeWidth={2}
              strokeDasharray="4 4"
              dot={false}
              isAnimationActive={false}
            />
          ) : null}
          <Line
            dataKey="value"
            type="monotone"
            stroke="var(--color-value)"
            strokeWidth={2.4}
            dot={false}
            isAnimationActive={false}
          />
          {paired ? (
            <Line
              dataKey="paired"
              type="monotone"
              stroke="var(--color-paired)"
              strokeWidth={2.4}
              dot={false}
              isAnimationActive={false}
            />
          ) : null}
        </LineChart>
      </ChartContainer>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-neutral-400">
        {canZoom ? (
          <span>Drag across the chart to zoom into those days</span>
        ) : null}
        {notes.map((note) => (
          <span
            key={`${note.date}-${note.label}`}
            className="flex items-center gap-1.5 text-neutral-500"
          >
            <span
              className="h-2.5 w-0.5"
              style={{ backgroundColor: ANNOTATION }}
            />
            {bucketLabel(note.date, "day")} · {note.label}
          </span>
        ))}
      </div>
    </div>
  );
};

interface BarsChartProps extends SettingsProps {
  result: ReportQueryResult;
  query?: ReportQuery;
  paired?: PairedResult;
  onDrill: (dimension: string, memberId: string) => void;
}

const ORDERS: { key: ReportRowOrder; label: string }[] = [
  { key: "given", label: "Default" },
  { key: "value", label: "By value" },
  { key: "label", label: "A–Z" }
];

/**
 * A ranked bar list, one row per member. With a comparison, a mark on each
 * bar is the previous period; with a paired measure, a second bar under
 * each is its value, and the previous period stays out.
 */
const BarsChart: React.FC<BarsChartProps> = ({
  result,
  query,
  paired,
  onDrill,
  settings,
  onSettingsChange
}) => {
  const measure = result.meta.measure;
  const unit = partsUnit(measure, query);
  const hasPrevious = result.totals.previous !== null && !paired;
  const [order, setOrder] = useSetting("order", "given", {
    settings,
    onSettingsChange
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const rows = useMemo(
    () =>
      foldRows(
        sortRows(result.rows, order),
        isExpanded ? Number.POSITIVE_INFINITY : BAR_FOLD,
        measure.kind
      ),
    [result.rows, order, isExpanded, measure.kind]
  );
  // The second measure beside each row, by the row's keys.
  const pairedValues = useMemo(() => {
    if (!paired) {
      return null;
    }
    const byKey = rowsByKey(paired.result.rows);
    const kept = new Set(rows.filter((r) => !r.isOther).map((r) => r.key));
    return new Map(
      rows.map((r) => [r.key, pairedRowValue(paired, byKey, r, kept)])
    );
  }, [paired, rows]);

  if (result.rows.length === 0) {
    return <Empty />;
  }

  const max = Math.max(
    ...rows.map((r) =>
      Math.max(r.value, r.previous ?? 0, pairedValues?.get(r.key) ?? 0)
    ),
    0.0001
  );
  const shown = rows.filter((r) => !r.isOther).length;
  const canFold = result.rows.length > BAR_FOLD + 1;

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-wrap items-center gap-3 text-[11px] text-neutral-500">
        {paired ? (
          <SeriesLegend
            entries={[
              { key: "value", label: measure.label, color: CURRENT },
              { key: "paired", label: paired.measure.label, color: PAIRED }
            ]}
          />
        ) : null}
        {hasPrevious ? (
          <>
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-3.5 rounded-[2px] bg-[#2a78d6]" />
              {`${result.meta.from} – ${result.meta.to}`}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-3 w-0.5 bg-neutral-900" />
              {`${result.meta.previous_from} – ${result.meta.previous_to}`}
            </span>
          </>
        ) : null}
        <span className="flex-1" />
        {result.rows.length > 1 ? (
          <Segmented options={ORDERS} value={order} onChange={setOrder} />
        ) : null}
        {canFold ? (
          <button
            type="button"
            onClick={() => setIsExpanded((v) => !v)}
            className="text-primary hover:underline"
          >
            {isExpanded
              ? `Top ${BAR_FOLD} only`
              : `Show all ${result.rows.length}`}
          </button>
        ) : null}
      </div>
      {rows.map((row, index) => {
        const delta = hasPrevious
          ? formatDelta(measure, row.value, row.previous)
          : null;
        const parts = shortParts(measure, row.parts);
        const pairedValue = pairedValues?.get(row.key) ?? null;
        const title = [
          rowTitle(measure, row.label, row, unit),
          ...(paired && pairedValue !== null
            ? [`${paired.measure.label}: ${formatValue(measure, pairedValue)}`]
            : [])
        ].join("\n");
        return (
          <button
            type="button"
            key={row.key}
            onClick={
              row.isOther ? undefined : () => drillRow(result, row, onDrill)
            }
            disabled={row.isOther}
            title={title}
            className={cn(
              "-mx-1.5 flex items-center gap-3 rounded-lg px-1.5 py-1 text-left text-[13px]",
              row.isOther ? "cursor-default" : "hover:bg-neutral-50"
            )}
          >
            <span
              className={cn(
                "w-[150px] shrink-0 truncate",
                row.isOther ? "text-neutral-400" : "text-neutral-700"
              )}
            >
              {row.label}
            </span>
            <span className="relative h-[18px] flex-1 overflow-hidden rounded bg-neutral-100">
              <span
                className={cn(
                  "absolute left-0 rounded",
                  paired ? "top-0 h-2" : "inset-y-0",
                  row.isOther
                    ? "bg-neutral-300"
                    : index === 0
                      ? "bg-[#2a78d6]"
                      : "bg-[#86b6ef]"
                )}
                // Width is data-driven.
                style={{ width: `${Math.max(1, (row.value / max) * 100)}%` }}
              />
              {pairedValue !== null ? (
                <span
                  className="absolute bottom-0 left-0 h-2 rounded"
                  // Width and colour are data-driven.
                  style={{
                    width: `${Math.max(1, (pairedValue / max) * 100)}%`,
                    backgroundColor: row.isOther ? OTHER_COLOR : PAIRED
                  }}
                />
              ) : null}
              {hasPrevious && row.previous !== null ? (
                <span
                  className="absolute inset-y-0 w-0.5 bg-neutral-900"
                  // Marker position is data-driven.
                  style={{ left: `${(row.previous / max) * 100}%` }}
                />
              ) : null}
            </span>
            <span className="w-[60px] shrink-0 text-right font-semibold text-neutral-900 tabular-nums">
              {formatValue(measure, row.value)}
            </span>
            {paired ? (
              <span className="w-[60px] shrink-0 text-right text-[12.5px] text-neutral-500 tabular-nums">
                {pairedValue === null ? "—" : formatValue(measure, pairedValue)}
              </span>
            ) : null}
            {measure.parts ? (
              <span className="w-[96px] shrink-0 text-right text-[11.5px] text-neutral-400 tabular-nums">
                {parts ?? "—"}
              </span>
            ) : null}
            {delta ? (
              <span
                className={cn(
                  "w-[56px] shrink-0 text-right text-[11.5px] tabular-nums",
                  delta.isFlat
                    ? "text-neutral-400"
                    : delta.isGood
                      ? "text-[#006300]"
                      : "text-[#a52a2a]"
                )}
              >
                {delta.text}
              </span>
            ) : null}
          </button>
        );
      })}
      <span className="text-[11px] text-neutral-400">
        {shown < result.rows.length && !rows.some((r) => r.isOther)
          ? `${result.rows.length - shown} more not shown · `
          : ""}
        Click a row to drill into it
      </span>
    </div>
  );
};

interface GridChartProps {
  result: ReportQueryResult;
  query?: ReportQuery;
  onDrill: (dimension: string, memberId: string) => void;
}

const GridChart: React.FC<GridChartProps> = ({ result, query, onDrill }) => {
  const measure = result.meta.measure;
  const unit = partsUnit(measure, query);
  const [rowDimension, columnDimension] = result.columns.map((c) => c.key);
  const rowKeys: string[] = [];
  const rowLabels = new Map<string, string>();
  const columnKeys: string[] = [];
  const columnLabels = new Map<string, string>();
  const cells = new Map<string, { value: number; parts: ReportParts | null }>();

  for (const row of result.rows) {
    const [rowKey, columnKey] = row.keys;
    if (!rowLabels.has(rowKey)) {
      rowKeys.push(rowKey);
      rowLabels.set(rowKey, row.labels[0]);
    }
    if (!columnLabels.has(columnKey)) {
      columnKeys.push(columnKey);
      columnLabels.set(columnKey, row.labels[1]);
    }
    cells.set(`${rowKey}|${columnKey}`, { value: row.value, parts: row.parts });
  }

  if (rowKeys.length === 0) {
    return <Empty />;
  }

  const values = [...cells.values()].map((c) => c.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const step = (v: number) =>
    Math.max(
      0,
      Math.min(5, Math.floor(((v - min) / Math.max(max - min, 0.0001)) * 6))
    );
  const gridTemplate = `140px repeat(${columnKeys.length}, minmax(0, 1fr))`;

  return (
    <div className="flex flex-col gap-1.5">
      <div
        className="grid items-center gap-1.5"
        // Column count is data-driven.
        style={{ gridTemplateColumns: gridTemplate }}
      >
        <span />
        {columnKeys.map((key) => (
          <span
            key={key}
            className="truncate text-center text-[11px] text-neutral-400 uppercase tracking-wider"
          >
            {columnLabels.get(key)}
          </span>
        ))}
      </div>
      {rowKeys.map((rowKey) => {
        const rowMax = Math.max(
          ...columnKeys.map((c) => cells.get(`${rowKey}|${c}`)?.value ?? -1)
        );
        return (
          <div
            key={rowKey}
            className="grid items-center gap-1.5"
            style={{ gridTemplateColumns: gridTemplate }}
          >
            <span className="truncate text-[12.5px] text-neutral-700">
              {rowLabels.get(rowKey)}
            </span>
            {columnKeys.map((columnKey) => {
              const cell = cells.get(`${rowKey}|${columnKey}`);
              if (cell === undefined) {
                return (
                  <span
                    key={columnKey}
                    className="h-9 rounded-md border border-neutral-100 border-dashed"
                  />
                );
              }
              const s = step(cell.value);
              return (
                <button
                  type="button"
                  key={columnKey}
                  onClick={() => {
                    onDrill(rowDimension, rowKey);
                    onDrill(columnDimension, columnKey);
                  }}
                  title={rowTitle(
                    measure,
                    `${rowLabels.get(rowKey)} · ${columnLabels.get(columnKey)}`,
                    { value: cell.value, previous: null, parts: cell.parts },
                    unit
                  )}
                  className={cn(
                    "flex h-9 items-center justify-center rounded-md font-semibold text-[12px] hover:outline hover:outline-2 hover:outline-neutral-900",
                    s >= 3 ? "text-white" : "text-[#0b3a6b]",
                    cell.value !== rowMax && "opacity-60"
                  )}
                  // Cell colour is data-driven.
                  style={{ backgroundColor: RAMP[s] }}
                >
                  {formatValue(measure, cell.value)}
                </button>
              );
            })}
          </div>
        );
      })}
      <div className="flex items-center justify-between pt-1.5 text-[11px] text-neutral-400">
        <span>Click a cell to drill into it</span>
        <span className="flex items-center gap-2">
          {formatValue(measure, min)}
          <span className="flex gap-0.5">
            {RAMP.map((color) => (
              <span
                key={color}
                className="h-2.5 w-3.5 rounded-[2px]"
                style={{ backgroundColor: color }}
              />
            ))}
          </span>
          {formatValue(measure, max)}
        </span>
      </div>
    </div>
  );
};

/** The bar data keys carry a prefix so a member id never collides with a field. */
const seriesField = (key: string): string => `v_${key}`;
const seriesKeyOf = (field: unknown): string =>
  typeof field === "string" && field.startsWith("v_") ? field.slice(2) : "";

interface StackTooltipProps {
  /** Filled in by the chart. */
  active?: boolean;
  payload?: ReadonlyArray<{
    dataKey?: string | number;
    value?: number | string;
    payload?: { entry?: string; label?: string };
  }>;
  measure: ReportMeasure;
  entries: StackEntry[];
  series: StackSeries[];
  colorOf: (key: string) => string;
  isStacked: boolean;
}

const StackTooltip: React.FC<StackTooltipProps> = ({
  active,
  payload,
  measure,
  entries,
  series,
  colorOf,
  isStacked
}) => {
  const first = payload?.[0]?.payload;
  if (!active || !payload?.length || !first?.entry) {
    return null;
  }
  const entry = entries.find((e) => e.key === first.entry);
  const total = payload.reduce((sum, item) => sum + Number(item.value ?? 0), 0);
  return (
    <TooltipBox title={first.label}>
      {payload.map((item) => {
        const key = seriesKeyOf(item.dataKey);
        const label = series.find((s) => s.key === key)?.label ?? key;
        const parts = entry
          ? shortParts(measure, entry.parts[key] ?? null)
          : null;
        return (
          <TooltipRow
            key={key}
            color={colorOf(key)}
            label={label}
            value={`${formatValue(measure, Number(item.value ?? 0))}${parts ? ` · ${parts}` : ""}`}
          />
        );
      })}
      {isStacked && payload.length > 1 ? (
        <TooltipRow label="Total" value={formatValue(measure, total)} isMuted />
      ) : null}
    </TooltipBox>
  );
};

interface StackChartProps extends SettingsProps {
  result: ReportQueryResult;
  onDrill: (dimension: string, memberId: string) => void;
}

type StackMode = "stacked" | "grouped";
const STACK_MODES: { key: StackMode; label: string }[] = [
  { key: "stacked", label: "Stacked" },
  { key: "grouped", label: "Grouped" }
];

/**
 * Two dimensions as bars: the first along the axis, the second as series,
 * stacked for a count or side by side for anything that cannot add up.
 * The legend hides a series, hovering it lights it up; a bar drills into
 * both members.
 */
const StackChart: React.FC<StackChartProps> = ({
  result,
  onDrill,
  settings,
  onSettingsChange
}) => {
  const measure = result.meta.measure;
  const [rowDimension, columnDimension] = result.columns
    .filter((c) => c.type === "dimension")
    .map((c) => c.key);
  const { entries, series } = useMemo(
    () => stackData(result, STACK_ENTRIES, STACK_SERIES, measure.kind),
    [result, measure.kind]
  );
  const [hidden, setHidden] = useSetting("hidden", [], {
    settings,
    onSettingsChange
  });
  const [stacked, setStacked] = useSetting(
    "stacked",
    measure.kind === "count",
    {
      settings,
      onSettingsChange
    }
  );
  const [highlight, setHighlight] = useState<string | null>(null);

  if (entries.length === 0) {
    return <Empty />;
  }

  const isStacked = stacked && measure.kind === "count";
  const visible = series.filter((s) => !hidden.includes(s.key));
  const colorOf = (key: string) =>
    seriesColor(
      series.findIndex((s) => s.key === key),
      key === OTHER_KEY
    );
  const data = entries.map((e) => ({
    entry: e.key,
    label: e.label,
    ...Object.fromEntries(
      series.map((s) => [seriesField(s.key), e.values[s.key] ?? 0])
    )
  }));
  const config: ChartConfig = Object.fromEntries(
    series.map((s) => [
      seriesField(s.key),
      { label: s.label, color: colorOf(s.key) }
    ])
  );
  const drill = (entryKey: string, seriesKey: string) => {
    if (!entryKey || entryKey === OTHER_KEY || seriesKey === OTHER_KEY) {
      return;
    }
    onDrill(rowDimension, entryKey);
    onDrill(columnDimension, seriesKey);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SeriesLegend
          entries={series.map((s) => ({
            key: s.key,
            label: s.label,
            color: colorOf(s.key),
            isHidden: hidden.includes(s.key)
          }))}
          onToggle={(key) => setHidden(toggled(hidden, key))}
          onHover={setHighlight}
        />
        {measure.kind === "count" ? (
          <Segmented
            options={STACK_MODES}
            value={isStacked ? "stacked" : "grouped"}
            onChange={(mode) => setStacked(mode === "stacked")}
          />
        ) : null}
      </div>
      <ChartContainer config={config} className="h-[320px] w-full">
        <BarChart
          data={data}
          margin={{ top: 12, right: 16, left: 0, bottom: 0 }}
          barCategoryGap="20%"
        >
          <CartesianGrid vertical={false} stroke="#f0f0f0" />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            interval={0}
            tick={{ fontSize: 11, fill: "#a3a3a3" }}
            tickFormatter={(label: string) =>
              label.length > 14 ? `${label.slice(0, 13)}…` : label
            }
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            width={44}
            tick={{ fontSize: 11, fill: "#a3a3a3" }}
            tickFormatter={(v: number) => formatValue(measure, v)}
          />
          <ChartTooltip
            cursor={{ fill: "#f5f5f5" }}
            content={
              <StackTooltip
                measure={measure}
                entries={entries}
                series={visible}
                colorOf={colorOf}
                isStacked={isStacked}
              />
            }
          />
          {visible.map((s) => (
            <Bar
              key={s.key}
              dataKey={seriesField(s.key)}
              name={s.label}
              stackId={isStacked ? "all" : undefined}
              fill={colorOf(s.key)}
              fillOpacity={highlight && highlight !== s.key ? 0.25 : 1}
              maxBarSize={48}
              isAnimationActive={false}
              cursor="pointer"
              onClick={(item: unknown) =>
                drill(keyOfItem(item, "entry"), s.key)
              }
            />
          ))}
        </BarChart>
      </ChartContainer>
      <span className="text-[11px] text-neutral-400">
        Click a bar to drill into it · click a legend entry to hide it
      </span>
    </div>
  );
};

interface ShareTooltipProps {
  /** Filled in by the chart. */
  active?: boolean;
  payload?: ReadonlyArray<{
    name?: string | number;
    value?: number | string;
    payload?: { fill?: string };
  }>;
  measure: Pick<ReportMeasure, "kind">;
  total: number;
}

const ShareTooltip: React.FC<ShareTooltipProps> = ({
  active,
  payload,
  measure,
  total
}) => {
  const item = payload?.[0];
  if (!active || !item) {
    return null;
  }
  const value = Number(item.value ?? 0);
  return (
    <TooltipBox title={String(item.name ?? "")}>
      <TooltipRow
        color={item.payload?.fill}
        label={formatValue(measure, value)}
        value={total > 0 ? `${Math.round((value / total) * 100)}%` : "—"}
      />
    </TooltipBox>
  );
};

interface ShareChartProps extends SettingsProps {
  result: ReportQueryResult;
  query?: ReportQuery;
  paired?: PairedResult;
  onDrill: (dimension: string, memberId: string) => void;
}

/** A pair's totals as the two slices of a pie. */
const pairRows = (
  measure: ReportMeasure,
  totals: ReportTotals,
  paired: PairedResult
): FoldedRow[] =>
  pairSlices(measure, totals.value, paired).map((slice) => ({
    ...slice,
    keys: [slice.key],
    previous: null,
    parts: null,
    isOther: false,
    folded: 0
  }));

const sentenceCase = (s: string): string =>
  s.charAt(0).toUpperCase() + s.slice(1);

/**
 * A ratio with no breakdown as its two parts: what was counted in and the
 * rest of the whole ("Checked in" 312, "Not checked in" 728).
 */
const partsRows = (
  measure: ReportMeasure,
  totals: ReportTotals
): FoldedRow[] => {
  if (!measure.parts || !totals.parts) {
    return [];
  }
  const { numerator, denominator } = totals.parts;
  const row = (key: string, label: string, value: number): FoldedRow => ({
    key,
    keys: [key],
    label,
    value,
    previous: null,
    parts: null,
    isOther: false,
    folded: 0
  });
  return [
    row("in", sentenceCase(measure.parts.numerator), numerator),
    row(
      "out",
      `Not ${measure.parts.numerator}`,
      Math.max(0, denominator - numerator)
    )
  ];
};

/**
 * One breakdown as shares of the whole: a donut with the largest members
 * and an "Other" fold, and the list beside it. Clicking a name hides it,
 * and the shares are recomputed over what is left; hovering a name lights
 * its slice up. A ratio with no breakdown is drawn as its two parts, and a
 * paired question as its two measures, whatever the breakdown.
 */
const ShareChart: React.FC<ShareChartProps> = ({
  result,
  query,
  paired,
  onDrill,
  settings,
  onSettingsChange
}) => {
  const measure = result.meta.measure;
  const dimension =
    result.columns.find((c) => c.type === "dimension")?.key ?? "";
  const isPair = !!paired;
  // The parts of a ratio are counts, whatever the measure reads as.
  const isParts = !isPair && dimension === "" && !!measure.parts;
  // Two fixed slices (a ratio's parts, a pair's measures) are neither
  // hidden nor drilled into.
  const isFixed = isParts || isPair;
  const rows = useMemo(
    () =>
      paired
        ? pairRows(measure, result.totals, paired)
        : isParts
          ? partsRows(measure, result.totals)
          : foldRows(result.rows, SHARE_SLICES, measure.kind),
    [paired, isParts, measure, result.totals, result.rows]
  );
  const [hidden, setHidden] = useSetting("hidden", [], {
    settings,
    onSettingsChange
  });
  const [highlight, setHighlight] = useState<string | null>(null);

  if (!isFixed && measure.kind !== "count") {
    return (
      <ReportEmptyState
        title="Shares need a count"
        hint="A duration is not part of a whole. Bars or a grid read better here."
      />
    );
  }
  if (!isFixed && dimension === "") {
    return (
      <ReportEmptyState
        title="Shares need a breakdown"
        hint="Pick a dimension to split the whole by."
      />
    );
  }
  if (rows.length === 0) {
    return <Empty />;
  }

  const sliceMeasure: Pick<ReportMeasure, "kind"> = isParts
    ? { kind: "count" }
    : measure;
  const visible = isFixed ? rows : rows.filter((r) => !hidden.includes(r.key));
  const total = visible.reduce((sum, r) => sum + r.value, 0);
  const colorOf = (key: string) =>
    isPair
      ? key === OWN_KEY
        ? CURRENT
        : PAIRED
      : isParts
        ? key === "in"
          ? CURRENT
          : OTHER_COLOR
        : seriesColor(
            rows.findIndex((r) => r.key === key),
            key === OTHER_KEY
          );
  const totalLabel =
    hidden.length > 0 && !isFixed
      ? "shown"
      : isParts
        ? (partsUnit(measure, query) ?? "total")
        : isPair
          ? "both together"
          : "total";
  // What the pie says in one line: a ratio's count, or the second measure
  // as a share of the first.
  const ratio = paired
    ? pairRatio(result.totals.value, paired.result.totals.value)
    : null;
  const note = paired
    ? ratio === null
      ? `Nothing counted for ${lowerFirst(measure.label)} in this period`
      : `${paired.measure.label}: ${Math.round(ratio * 100)}% of ${lowerFirst(measure.label)}`
    : isParts
      ? `${formatValue(measure, result.totals.value)} ${measure.parts?.numerator ?? ""}`
      : "Click a name to take it out of the whole";

  // The list sits beside the donut only where the container leaves it room
  // (Explore, a wide tile); in a narrow tile the donut stands alone and the
  // tooltip names the slices.
  return (
    <div className="@container">
      <div className="flex @lg:flex-row flex-col items-center gap-3">
        <div className="relative h-[280px] w-full max-w-[320px] shrink-0">
          <ChartContainer config={{}} className="aspect-auto h-full w-full">
            <PieChart>
              <ChartTooltip
                content={<ShareTooltip measure={sliceMeasure} total={total} />}
              />
              <Pie
                data={visible.map((r) => ({
                  key: r.key,
                  name: r.label,
                  value: r.value
                }))}
                dataKey="value"
                nameKey="name"
                innerRadius={70}
                outerRadius={110}
                paddingAngle={1}
                stroke="#ffffff"
                isAnimationActive={false}
                cursor={isFixed ? "default" : "pointer"}
                onClick={(item: unknown) => {
                  const key = keyOfItem(item, "key");
                  const picked = rows.find((r) => r.key === key);
                  if (!isFixed && picked && key !== OTHER_KEY) {
                    drillRow(result, picked, onDrill);
                  }
                }}
              >
                {visible.map((r) => (
                  <Cell
                    key={r.key}
                    fill={colorOf(r.key)}
                    fillOpacity={highlight && highlight !== r.key ? 0.25 : 1}
                  />
                ))}
              </Pie>
            </PieChart>
          </ChartContainer>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <span className="font-semibold text-[22px] text-neutral-800 tabular-nums">
              {formatValue(sliceMeasure, total)}
            </span>
            <span className="text-[11px] text-neutral-400">{totalLabel}</span>
          </div>
        </div>
        <div className="@lg:flex hidden min-w-0 flex-1 flex-col gap-0.5">
          {rows.map((row) => {
            const isHidden = !isFixed && hidden.includes(row.key);
            const share = total > 0 && !isHidden ? row.value / total : null;
            return (
              <div
                key={row.key}
                className="flex items-center gap-2 text-[12.5px]"
                onMouseEnter={() => setHighlight(isHidden ? null : row.key)}
                onMouseLeave={() => setHighlight(null)}
              >
                <button
                  type="button"
                  disabled={isFixed}
                  onClick={() => setHidden(toggled(hidden, row.key))}
                  title={
                    isFixed
                      ? undefined
                      : isHidden
                        ? "Show"
                        : "Hide from the whole"
                  }
                  className={cn(
                    "flex min-w-0 flex-1 items-center gap-2 rounded-md px-1.5 py-1 text-left",
                    isFixed ? "cursor-default" : "hover:bg-neutral-50",
                    isHidden && "opacity-50"
                  )}
                >
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
                    style={{ backgroundColor: colorOf(row.key) }}
                  />
                  <span
                    className={cn(
                      "flex-1 truncate text-neutral-700",
                      isHidden && "line-through"
                    )}
                  >
                    {row.label}
                  </span>
                  <span className="w-[70px] shrink-0 text-right font-semibold text-neutral-900 tabular-nums">
                    {formatValue(sliceMeasure, row.value)}
                  </span>
                  <span className="w-[44px] shrink-0 text-right text-neutral-500 tabular-nums">
                    {share === null ? "—" : `${Math.round(share * 100)}%`}
                  </span>
                </button>
                {row.isOther || isFixed ? (
                  <span className="w-[34px]" />
                ) : (
                  <button
                    type="button"
                    onClick={() => drillRow(result, row, onDrill)}
                    title={`Drill into ${row.label}`}
                    className="w-[34px] shrink-0 text-[11px] text-primary hover:underline"
                  >
                    drill
                  </button>
                )}
              </div>
            );
          })}
          <span className="pt-1 text-[11px] text-neutral-400">{note}</span>
        </div>
      </div>
    </div>
  );
};

interface GaugeChartProps {
  result: ReportQueryResult;
  query?: ReportQuery;
  target?: number | null;
  paired?: PairedResult;
}

// The ring is drawn in a 200 × 118 box: a half circle around (100, 100),
// with the end labels below it.
const GAUGE_CENTER_X = 100;
const GAUGE_CENTER_Y = 100;
const GAUGE_RADIUS = 84;
const GAUGE_STROKE = 18;
const GAUGE_LABEL_Y = 114;
const TARGET_MET = "#1a9a3c";

/** Where a fraction of the way from left (0) to right (1) lands on the ring. */
const gaugePoint = (fraction: number, radius: number) => {
  const angle = Math.PI * (1 - fraction);
  return {
    x: GAUGE_CENTER_X + radius * Math.cos(angle),
    y: GAUGE_CENTER_Y - radius * Math.sin(angle)
  };
};

/**
 * An SVG path along the ring, from the left end to `fraction` of the way,
 * clockwise over the top. The ring is half a circle, so the arc is never
 * the long way round.
 */
const gaugeArc = (fraction: number): string => {
  const start = gaugePoint(0, GAUGE_RADIUS);
  const end = gaugePoint(fraction, GAUGE_RADIUS);
  return `M ${start.x} ${start.y} A ${GAUGE_RADIUS} ${GAUGE_RADIUS} 0 0 1 ${end.x} ${end.y}`;
};

interface GaugeRingProps {
  /** How much of the ring is filled, 0..1. */
  fraction: number;
  /** Where the target mark sits on the ring, 0..1; null for none. */
  targetFraction: number | null;
  /** Whether the target is reached, which turns the fill green. */
  isMet: boolean;
  /** The number inside the ring, and the reading a screen reader gets. */
  value: string;
  label: string;
}

/** The half ring itself, with the number on its baseline. */
const GaugeRing: React.FC<GaugeRingProps> = ({
  fraction,
  targetFraction,
  isMet,
  value,
  label
}) => {
  const mark =
    targetFraction !== null
      ? {
          inner: gaugePoint(
            targetFraction,
            GAUGE_RADIUS - GAUGE_STROKE / 2 - 4
          ),
          outer: gaugePoint(targetFraction, GAUGE_RADIUS + GAUGE_STROKE / 2 + 4)
        }
      : null;
  return (
    <div className="relative w-full max-w-[360px]">
      <svg
        viewBox="0 0 200 118"
        className="block w-full"
        role="img"
        aria-label={`${label}: ${value}`}
      >
        <path
          d={gaugeArc(1)}
          fill="none"
          stroke={EMPTY_CELL}
          strokeWidth={GAUGE_STROKE}
          strokeLinecap="round"
        />
        {fraction > 0 ? (
          <path
            d={gaugeArc(fraction)}
            fill="none"
            stroke={isMet ? TARGET_MET : CURRENT}
            strokeWidth={GAUGE_STROKE}
            strokeLinecap="round"
          />
        ) : null}
        {mark ? (
          <line
            x1={mark.inner.x}
            y1={mark.inner.y}
            x2={mark.outer.x}
            y2={mark.outer.y}
            stroke="#171717"
            strokeWidth={2.5}
            strokeLinecap="round"
          />
        ) : null}
        <text
          x={gaugePoint(0, GAUGE_RADIUS).x}
          y={GAUGE_LABEL_Y}
          textAnchor="middle"
          fontSize={7}
          fill="#a3a3a3"
        >
          0%
        </text>
        <text
          x={gaugePoint(1, GAUGE_RADIUS).x}
          y={GAUGE_LABEL_Y}
          textAnchor="middle"
          fontSize={7}
          fill="#a3a3a3"
        >
          100%
        </text>
      </svg>
      {/* The number sits on the ring's baseline, which is 100/118 of the way down. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-[15%] flex flex-col items-center">
        <span className="font-semibold text-[36px] text-neutral-800 tabular-nums leading-[40px]">
          {value}
        </span>
      </div>
    </div>
  );
};

/**
 * A ratio as a half ring filled to its value, with the number inside, the
 * two numbers it stands on beneath, and the change against the previous
 * period. A tile's target is a mark on the ring; the fill turns green once
 * it is reached. A paired question reads the second count as a share of
 * the first: "612 requests resolved of 686 requests filed" is 89%.
 */
const GaugeChart: React.FC<GaugeChartProps> = ({
  result,
  query,
  target,
  paired
}) => {
  const measure = result.meta.measure;
  if (paired) {
    if (measure.kind !== "count") {
      return (
        <ReportEmptyState
          title="A paired gauge reads one count as a share of the other"
          hint="These two measures are not counts. A trend or bars read better here."
        />
      );
    }
    const own = result.totals.value;
    const other = paired.result.totals.value;
    const ratio = pairRatio(own, other);
    return (
      <div className="flex flex-col items-center gap-1.5 py-3">
        <GaugeRing
          fraction={ratio === null ? 0 : Math.min(1, ratio)}
          targetFraction={null}
          isMet={false}
          value={ratio === null ? "—" : `${Math.round(ratio * 100)}%`}
          label={`${paired.measure.label} as a share of ${lowerFirst(measure.label)}`}
        />
        <span className="text-[12px] text-neutral-500 tabular-nums">
          {`${formatValue(measure, other)} ${lowerFirst(paired.measure.label)} of ${formatValue(measure, own)} ${lowerFirst(measure.label)}`}
        </span>
        {ratio === null ? (
          <span className="text-[11.5px] text-neutral-400">
            Nothing to compare against in this period
          </span>
        ) : null}
      </div>
    );
  }
  if (measure.kind !== "percent") {
    return (
      <ReportEmptyState
        title="A gauge reads a percentage"
        hint="A count or a duration has no full mark. A trend or bars read better here."
      />
    );
  }
  const delta = formatDelta(
    measure,
    result.totals.value,
    result.totals.previous
  );
  const parts = formatParts(
    measure,
    result.totals.parts,
    partsUnit(measure, query)
  );
  const hasTarget = typeof target === "number";
  const isMet =
    hasTarget &&
    (measure.good_when === "down"
      ? result.totals.value <= target
      : result.totals.value >= target);

  return (
    <div className="flex flex-col items-center gap-1.5 py-3">
      <GaugeRing
        fraction={Math.max(0, Math.min(1, result.totals.value))}
        targetFraction={hasTarget ? Math.max(0, Math.min(1, target)) : null}
        isMet={isMet}
        value={formatValue(measure, result.totals.value)}
        label={measure.label}
      />
      {parts ? (
        <span className="text-[12px] text-neutral-500 tabular-nums">
          {parts}
        </span>
      ) : null}
      <div className="flex items-center gap-2 text-[11.5px]">
        {delta ? (
          <span
            className={cn(
              "rounded-full px-2 py-0.5 tabular-nums",
              delta.isFlat
                ? "bg-neutral-100 text-neutral-500"
                : delta.isGood
                  ? "bg-[#e8f6e8] text-[#006300]"
                  : "bg-[#fbe9e9] text-[#a52a2a]"
            )}
          >
            {delta.text} vs previous
          </span>
        ) : null}
        {hasTarget ? (
          <span className={isMet ? "text-[#006300]" : "text-neutral-500"}>
            {isMet ? "✓ " : ""}Target {formatValue(measure, target)}
          </span>
        ) : null}
      </div>
    </div>
  );
};

interface CalendarChartProps {
  result: ReportQueryResult;
  query?: ReportQuery;
  onZoom?: (period: ReportPeriod) => void;
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * One cell per day across the period, weeks as columns, Monday on top:
 * the way seasonality and quiet weeks read. A click narrows to that day.
 */
const CalendarChart: React.FC<CalendarChartProps> = ({
  result,
  query,
  onZoom
}) => {
  const measure = result.meta.measure;
  const unit = partsUnit(measure, query);
  if (result.meta.grain !== "day") {
    return (
      <ReportEmptyState
        title="The calendar reads day by day"
        hint={`This answer is by ${result.meta.grain}. Pick the calendar again to ask it by day.`}
      />
    );
  }
  const weeks = calendarWeeks(result.series, result.meta.from, result.meta.to);
  const values = weeks
    .flatMap((w) => w.cells)
    .map((c) => c.value)
    .filter((v): v is number => v !== null);
  if (values.length === 0) {
    return <Empty />;
  }
  const max = Math.max(...values, 0.0001);
  const colorOf = (value: number | null): string => {
    if (value === null) {
      return "transparent";
    }
    if (value <= 0) {
      return EMPTY_CELL;
    }
    return RAMP[
      Math.min(RAMP.length - 1, Math.floor((value / max) * RAMP.length))
    ];
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="overflow-x-auto">
        <div className="flex gap-1">
          <div className="flex flex-col gap-1 pt-[18px]">
            {WEEKDAYS.map((day) => (
              <span
                key={day}
                className="flex h-[22px] w-8 items-center text-[10px] text-neutral-400"
              >
                {day}
              </span>
            ))}
          </div>
          {weeks.map((week) => (
            <div key={week.start} className="flex flex-col gap-1">
              <span className="h-[14px] text-[10px] text-neutral-400">
                {week.monthLabel ?? ""}
              </span>
              {week.cells.map((cell) => {
                const title =
                  cell.value === null
                    ? cell.day
                    : rowTitle(
                        measure,
                        bucketTitle(cell.day, "day"),
                        {
                          value: cell.value,
                          previous: null,
                          parts: cell.parts
                        },
                        unit
                      );
                return (
                  <button
                    type="button"
                    key={cell.day}
                    title={title}
                    disabled={!onZoom || cell.value === null}
                    onClick={() =>
                      onZoom?.({
                        preset: "custom",
                        from: cell.day,
                        to: cell.day
                      })
                    }
                    className={cn(
                      "h-[22px] w-[22px] rounded-[4px]",
                      cell.value === null
                        ? "border border-neutral-100 border-dashed"
                        : onZoom &&
                            "hover:outline hover:outline-2 hover:outline-neutral-900"
                    )}
                    // Cell colour is data-driven.
                    style={{ backgroundColor: colorOf(cell.value) }}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between text-[11px] text-neutral-400">
        <span>
          {onZoom ? "Click a day to zoom into it" : "One cell per day"}
        </span>
        <span className="flex items-center gap-2">
          {formatValue(measure, 0)}
          <span className="flex gap-0.5">
            {RAMP.map((color) => (
              <span
                key={color}
                className="h-2.5 w-3.5 rounded-[2px]"
                style={{ backgroundColor: color }}
              />
            ))}
          </span>
          {formatValue(measure, max)}
        </span>
      </div>
    </div>
  );
};

export const ResultChart: React.FC<ResultChartProps> = ({
  form,
  result,
  query,
  target,
  paired,
  onDrill,
  onZoom,
  settings,
  onSettingsChange,
  height
}) => {
  const knobs = { settings, onSettingsChange };
  switch (form) {
    case "bars":
      return (
        <BarsChart
          result={result}
          query={query}
          paired={paired}
          onDrill={onDrill}
          {...knobs}
        />
      );
    case "grid":
      return <GridChart result={result} query={query} onDrill={onDrill} />;
    case "stack":
      return <StackChart result={result} onDrill={onDrill} {...knobs} />;
    case "share":
      return (
        <ShareChart
          result={result}
          query={query}
          paired={paired}
          onDrill={onDrill}
          {...knobs}
        />
      );
    case "gauge":
      return (
        <GaugeChart
          result={result}
          query={query}
          target={target}
          paired={paired}
        />
      );
    case "calendar":
      return <CalendarChart result={result} query={query} onZoom={onZoom} />;
    default:
      return (
        <TrendChart
          result={result}
          query={query}
          paired={paired}
          onZoom={onZoom}
          height={height}
          {...knobs}
        />
      );
  }
};

export { CURRENT, CURRENT_SOFT };
